import Symbol from './Symbol';
import SymbolMap from './SymbolMap';
import NumberPad from './NumberPad';
import ComboBadge from './ComboBadge';
import { type Feedback } from '@/pages/dsst-shinhan/dsstConfig';

interface GameScreenProps {
  symbolMap: number[];
  currentSymbol: number;
  selectedNumber: number | null;
  feedback: Feedback;
  remainingSeconds: number;
  combo: number;
  setChangeNotice: boolean;
  setIndex: number;
  numberOrder: number[];
  onSelect: (n: number) => void;
}

export default function GameScreen({
  symbolMap,
  currentSymbol,
  selectedNumber,
  feedback,
  remainingSeconds,
  combo,
  setChangeNotice,
  setIndex,
  numberOrder,
  onSelect,
}: GameScreenProps) {
  const isLastTen = remainingSeconds <= 10;
  const isLastFive = remainingSeconds <= 5;
  const timeText = `00:${String(remainingSeconds).padStart(2, '0')}`;

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-background-50 to-secondary-50 px-5 pt-[var(--dsst-pt)] pb-[var(--dsst-pb)]">
      {/* ① 게임명 + 남은 시간 */}
      <div className="shrink-0 flex items-center justify-between">
        {setChangeNotice ? (
          <span className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-primary-600 text-white text-[13px] font-bold whitespace-nowrap animate-combo-badge-milestone">
            <i className="ri-refresh-line text-sm leading-none" />
            기호 그룹 교체!
          </span>
        ) : (
          <span className="inline-flex items-center h-7 px-3 rounded-full bg-primary-50 text-primary-700 text-[13px] font-bold whitespace-nowrap">
            기호 맞추기
          </span>
        )}
        <span
          className={`tabular-nums leading-none whitespace-nowrap ${
            isLastFive
              ? 'text-3xl font-extrabold text-primary-700 animate-timer-pulse'
              : isLastTen
                ? 'text-2xl font-extrabold text-primary-600'
                : 'text-2xl font-semibold text-foreground-800'
          }`}
        >
          {timeText}
        </span>
      </div>

      {/* ② 기호-숫자 대응표 */}
      <div className="mt-3 shrink-0 rounded-2xl bg-primary-50 border border-primary-100 px-3 py-3">
        <SymbolMap
          symbolMap={symbolMap}
          setIndex={setIndex}
          symbolSizeClass="w-[var(--dsst-map-symbol)] h-[var(--dsst-map-symbol)]"
        />
      </div>

      {/* 연속 정답(Combo) — 3연속부터 노출 */}
      <ComboBadge combo={combo} />

      {/* ③ 현재 문제 기호 */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center">
        <p className="text-[15px] font-semibold text-foreground-700 text-center leading-snug">
          이 기호와 짝이 되는 숫자는?
        </p>
        <div
          key={currentSymbol}
          className={`mt-3 flex items-center justify-center text-primary-600 ${
            feedback === 'correct' ? 'animate-symbol-correct' : 'animate-symbol-in'
          }`}
        >
          <Symbol
            symbolId={currentSymbol}
            setIndex={setIndex}
            className="w-[var(--dsst-symbol-size)] h-[var(--dsst-symbol-size)]"
          />
        </div>

        {/* ④ 정답/오답 즉각 피드백 */}
        <div className="mt-4 flex items-center justify-center h-10">
          {feedback === 'correct' && (
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-base font-bold whitespace-nowrap animate-combo-bump">
              정답
            </span>
          )}
          {feedback === 'wrong' && (
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-base font-bold whitespace-nowrap animate-combo-bump">
              오답
            </span>
          )}
        </div>
      </div>

      {/* ⑤ 숫자 선택 */}
      <div className="shrink-0 mt-2">
        <NumberPad
          onSelect={onSelect}
          selectedNumber={selectedNumber}
          feedback={feedback}
          disabled={feedback !== null}
          order={numberOrder}
        />
      </div>
    </div>
  );
}